/** What a sound effect is, and the helpers they are all written with. Pure. */

import { midiToHz, parseNote } from '../notes';
import type { Rand } from '../rand';
import { adsr, type Adsr, type VoiceSpec, type Wave } from '../voice';

/**
 * A sound effect: its voices, from a seeded variation source and one parameter (see `paramOf` in
 * scan.ts; 0 where a sound has none). Starting times are relative to the moment it is played.
 */
export type Sfx = (r: Rand, n: number) => VoiceSpec[];

/**
 * A struck voice - a ping, a thump, a crack - that dies away over `decay` seconds. The gate is
 * held through the decay: the release begins at gate-off, so a short gate would cut the decay off.
 */
export function hit(wave: Wave, freq: number, decay: number, level: number): VoiceSpec {
  return { wave, at: 0, freq, gate: decay, env: { a: 0.001, d: decay, s: 0, r: Math.min(0.05, decay * 0.25) }, level };
}

/** A plain voice starting at 0; spread extra fields (filter, glide, width ...) over it. */
export function tone(wave: Wave, freq: number, gate: number, env: Adsr, level: number): VoiceSpec {
  return { wave, at: 0, freq, gate, env, level };
}

/** Pitches in turn on one voice, `stepTime` apart, holding the last: the board's stepped pitch. */
export function run(wave: Wave, midis: readonly number[], stepTime: number, gate: number, level: number): VoiceSpec {
  const steps = midis.map(midiToHz);
  return { ...tone(wave, steps[0] ?? 440, gate, adsr(0.001, 0.1, 0.35, 0.06), level), steps, stepTime, once: true };
}

/** How a line of a jingle sounds: a voice without its pitch and timing. */
export type Patch = Omit<VoiceSpec, 'at' | 'gate' | 'freq'>;

/** The jingles' three voices, as the arcade board had three: a bright lead, a hollow second, a round bass. */
export const LEAD: Patch = { wave: 'pulse', width: 0.25, env: adsr(0.002, 0.09, 0.55, 0.03), level: 0.2 };
export const SECOND: Patch = { wave: 'pulse', width: 0.5, env: adsr(0.002, 0.09, 0.5, 0.03), level: 0.06 };
export const BASS: Patch = { wave: 'triangle', env: adsr(0.002, 0.12, 0.7, 0.03), level: 0.26 };

/** A note of a line is silent this long before the next begins, so the two never count as sounding together. */
const CLEAR = 0.002;

/**
 * One line of a jingle, a voice per note, written as the music is (music/types.ts): a token per
 * step of `step` seconds - a note name, `.` to hold, `-` to rest; `|` is only for the eye. Each
 * note is let go in time to be silent when the next begins, so a line holds one voice of the cap
 * (for steps of 55 ms and more: a shorter note cannot be let go any sooner).
 */
export function tune(line: string, step: number, patch: Patch, at = 0): VoiceSpec[] {
  const out: VoiceSpec[] = [];
  const tokens = line.trim().split(/\s+/).filter((t) => t !== '|');
  let from = -1;
  let midi = 0;
  const close = (end: number): void => {
    if (from < 0) return;
    const gate = Math.max(0.02, (end - from) * step - patch.env.r - CLEAR);
    out.push({ ...patch, at: at + from * step, gate, freq: midiToHz(midi) });
    from = -1;
  };
  tokens.forEach((t, i) => {
    if (t === '.') return;
    close(i);
    if (t === '-') return;
    const note = parseNote(t);
    if (note === null) throw new Error(`bad note "${t}"`);
    from = i;
    midi = note;
  });
  close(tokens.length);
  return out;
}
